package dev.lingvo.site.pages.app.projects.slug.screenshots

import com.varabyte.kobweb.core.PageContext
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.nav.UrlState

/** Area destinations are real pages so shared links retain browser navigation semantics. */
enum class ScreenshotsTab { PLAN, DESIGN, REVIEW, UPDATES }

/** A drawer cell identifies a stable slot, never its reorderable position. */
data class ScreenshotsCell(val slotId: String, val locale: String)

/** URL-owned state is independent of page loading and can be validated against fresh inventories. */
data class ScreenshotsAreaState(
    val platform: StorePlatform = StorePlatform.APP_STORE,
    val device: DeviceClass? = null,
    val cell: ScreenshotsCell? = null,
    val slot: String? = null,
    val locale: String? = null,
    val layer: String? = null,
    val theme: String = "light",
    val category: String? = null,
) {
    /** Writes only the selected page's parameters, keeping transient editor geometry out of links. */
    fun toParams(tab: ScreenshotsTab? = null): Map<String, String?> = buildMap {
        put("platform", platform.id)
        if (tab == ScreenshotsTab.UPDATES) return@buildMap
        put("device", device?.takeIf { it.platform == platform }?.id)
        if (tab == null || tab == ScreenshotsTab.PLAN) put("cell", cell?.let { "${it.slotId}:${it.locale}" })
        if (tab == null || tab == ScreenshotsTab.DESIGN) {
            put("slot", slot)
            put("layer", layer)
        }
        if (tab == null || tab != ScreenshotsTab.PLAN) put("locale", locale)
        if (tab == null || tab == ScreenshotsTab.REVIEW) put("theme", theme.takeIf { it == "dark" } ?: "light")
        if (tab == null) put("category", category)
    }

    /** Syntactic and inventory checks retire stale links rather than preventing the page from opening. */
    companion object {
        /** Null inventories mean data has not loaded; supplied inventories retire references no longer present. */
        fun parse(
            params: Map<String, String?>, slots: Set<String>? = null, locales: Set<String>? = null,
            layers: Set<String>? = null, defaultPlatform: StorePlatform = StorePlatform.APP_STORE,
        ): ScreenshotsAreaState {
            val platform = StorePlatform.entries.firstOrNull { it.id == params["platform"] } ?: defaultPlatform
            val device = params["device"]?.let(DeviceClass::fromId)?.takeIf { it.platform == platform }
            fun id(value: String?, inventory: Set<String>?): String? = value?.takeIf {
                Regex("^[A-Za-z0-9_-]{1,64}$").matches(it) && (inventory == null || it in inventory)
            }
            fun locale(value: String?): String? = value?.takeIf {
                Regex("^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8}){0,2}$").matches(it) && (locales == null || it in locales)
            }
            val cell = params["cell"]?.split(':')?.takeIf { it.size == 2 }?.let { parts ->
                val slot = id(parts[0], slots)
                val language = locale(parts[1])
                if (slot != null && language != null) ScreenshotsCell(slot, language) else null
            }
            return ScreenshotsAreaState(platform, device, cell, id(params["slot"], slots), locale(params["locale"]),
                id(params["layer"], layers), params["theme"].takeIf { it == "dark" } ?: "light",
                params["category"]?.takeIf { it in setOf("layout", "minimal", "bold", "dark", "playful") })
        }
    }
}

/** Reads through the shared URL adapter so all screenshot screens interpret navigation consistently. */
fun readScreenshotsAreaState(
    ctx: PageContext, slots: Set<String>? = null, locales: Set<String>? = null, layers: Set<String>? = null,
): ScreenshotsAreaState = ScreenshotsAreaState.parse(
    listOf("platform", "device", "cell", "slot", "locale", "layer", "theme", "category")
        .associateWith { UrlState.read(ctx, it) }, slots, locales, layers,
)

/** Builds page links with the same encoding and query ordering used by live URL state. */
fun screenshotsPath(slug: String, tab: ScreenshotsTab, params: ScreenshotsAreaState = ScreenshotsAreaState()): String {
    val suffix = when (tab) {
        ScreenshotsTab.PLAN -> ""
        ScreenshotsTab.DESIGN -> "/design"
        ScreenshotsTab.REVIEW -> "/review"
        ScreenshotsTab.UPDATES -> "/updates"
    }
    return "/app/projects/${encodeURIComponent(slug)}/screenshots$suffix" + UrlState.toQuery(params.toParams(tab))
}

/** Template selection carries only its documented return slot and category. */
fun screenshotsTemplatesPath(slug: String, params: ScreenshotsAreaState = ScreenshotsAreaState()): String =
    "/app/projects/${encodeURIComponent(slug)}/screenshots/templates" + UrlState.toQuery(
        mapOf("platform" to params.platform.id, "category" to params.category, "slot" to params.slot),
    )

/** The retake console returns to a stable cell on the plan. */
fun screenshotsAgentsPath(slug: String, params: ScreenshotsAreaState = ScreenshotsAreaState()): String =
    "/app/projects/${encodeURIComponent(slug)}/screenshots/agents" + UrlState.toQuery(mapOf("platform" to params.platform.id))

private external fun encodeURIComponent(value: String): String
